import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { search } from "@/lib/api";

const resultHref = (r) =>
  r.type === "treatment" ? `/treatments/${r.slug}` : r.type === "journal" ? `/journal/${r.slug}` : "/art";

const RESULT_LABELS = { treatment: "Treatment", journal: "Journal" };
const resultLabel = (r) => RESULT_LABELS[r.type] || "Art";

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    if (!open) { setQ(""); setResults([]); }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const d = await search(q);
        setResults(d.results || []);
      } catch (error) {
        console.error("Search failed:", error);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-50 bg-[#F7F6F2]"
        >
          <div className="max-w-[1100px] mx-auto px-6 sm:px-10 pt-10">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">Search Studio A</span>
              <button
                onClick={onClose}
                aria-label="Close search"
                data-testid="search-close"
                className="h-10 w-10 grid place-items-center"
              >
                <X size={20} />
              </button>
            </div>
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="Try ‘rosacea’, ‘breathwork’, ‘nervous system’…"
              className="mt-8 w-full bg-transparent border-b border-[#D5CDBF] focus:border-[#2E2B27] outline-none serif text-4xl sm:text-6xl py-6 placeholder:text-[#6E6A63]"
            />

            <div className="mt-10">
              {!q && (
                <p className="text-[14px] text-[#55524C] max-w-xl">
                  Search our treatments, journal and artworks. Try a skin concern (rosacea, eczema), a feeling (stillness, burnout) or a treatment name.
                </p>
              )}
              {q && results.length === 0 && (
                <p className="text-[14px] text-[#55524C]">No matches yet. Try a different word.</p>
              )}
              <ul className="divide-y divide-[#D5CDBF]">
                {results.map((r) => (
                  <li key={`${r.type}-${r.slug}`}>
                    <Link
                      to={resultHref(r)}
                      onClick={onClose}
                      data-testid={`search-result-${r.slug}`}
                      className="flex items-baseline justify-between py-5 hover:text-[#A96A53] transition-colors"
                    >
                      <span className="serif text-2xl">{r.title}</span>
                      <span className="label-eyebrow">{resultLabel(r)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
